<template>
  <z-drawer
    v-model="visible"
    :title="t('z.table.custom.title')"
    :size="drawerSize"
    :direction="drawerDirection"
    destroy-on-close
    append-to-body
    :class="ns.b()"
    :body-class="ns.e('body')"
    :footer-class="ns.e('footer')"
    @closed="$grid.closeCustom?.()"
  >
    <div :class="ns.e('table')">
      <div :class="ns.e('thead')">
        <div :class="ns.e('tr')">
          <div :class="[ns.e('th'), ns.e('th-check')]">
            <z-checkbox
              :model-value="allChecked"
              :indeterminate="indeterminate"
              @update:model-value="toggleAll"
            >
              {{ t('z.table.custom.all') }}
            </z-checkbox>
          </div>
          <div :class="[ns.e('th'), ns.e('th-title')]">
            {{ t('z.table.custom.columnTitle') }}
          </div>
          <div :class="[ns.e('th'), ns.e('th-width')]">
            {{ t('z.table.custom.columnWidth') }}
          </div>
          <div :class="[ns.e('th'), ns.e('th-fixed')]">
            {{ t('z.table.custom.freezeColumn', { count: maxFixedCount }) }}
          </div>
        </div>
      </div>

      <div ref="listRef" :class="ns.e('tbody')">
        <div
          v-for="(col, index) in store.columns"
          :key="col.id"
          :class="[
            ns.e('tr'),
            ns.is('highlight', col.id === store.highlightColumnId),
            ns.is('hidden', col.checked === false),
            ns.is('dragging', dragIndex === index),
            ns.is('drag-over', dragOverIndex === index),
          ]"
          :data-column-id="col.id"
          :draggable="canDrag"
          @dragstart="onDragStart(index, $event)"
          @dragover="onDragOver(index, $event)"
          @drop="onDrop(index)"
          @dragend="onDragEnd"
        >
          <div :class="[ns.e('td'), ns.e('td-check')]">
            <z-checkbox
              :model-value="col.checked !== false"
              :disabled="isToggleDisabled(col)"
              @update:model-value="(val) => onToggleColumn(col, val)"
              @click.stop
            />
          </div>
          <div :class="[ns.e('td'), ns.e('td-title')]">
            <z-tooltip
              v-if="sideBar.draggable"
              placement="top"
              :content="canDrag ? t('z.table.custom.dragTip') : t('z.table.custom.dragDisabledTip')"
            >
              <span
                :class="[ns.e('drag-handle'), { [ns.is('disabled')]: !canDrag }]"
                aria-hidden="true"
              >
                <z-icon :size="16">
                  <Rank />
                </z-icon>
              </span>
            </z-tooltip>
            <span :class="ns.e('label-text')">{{ columnLabel(col) }}</span>
          </div>
          <div :class="[ns.e('td'), ns.e('td-width')]">
            <z-input-number
              :model-value="resolveWidth(col)"
              size="small"
              :min="40"
              :max="2000"
              :step="1"
              controls-position="right"
              @update:model-value="(val) => onWidthChange(col, val)"
            />
          </div>
          <div :class="[ns.e('td'), ns.e('td-fixed')]">
            <z-segmented
              v-if="!isPinDisabled(col)"
              :model-value="col.fixed ?? ''"
              size="small"
              block
              :options="pinOptions(col)"
              @update:model-value="(val) => onFixedChange(col, val as GridFixed)"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <z-button @click="$grid.resetCustom?.()">
        {{ t('z.table.custom.reset') }}
      </z-button>
      <z-button @click="$grid.cancelCustom?.()">
        {{ t('z.table.custom.cancel') }}
      </z-button>
      <z-button type="primary" @click="$grid.confirmCustom?.()">
        {{ t('z.table.custom.confirm') }}
      </z-button>
    </template>
  </z-drawer>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from 'vue'
import ZButton from '@zoryn/components/button'
import ZCheckbox from '@zoryn/components/checkbox'
import ZDrawer from '@zoryn/components/drawer'
import ZIcon from '@zoryn/components/icon'
import ZInputNumber from '@zoryn/components/input-number'
import { ZMessage } from '@zoryn/components/message'
import ZSegmented from '@zoryn/components/segmented'
import ZTooltip from '@zoryn/components/tooltip'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { Rank } from '@zoryn/icons'
import { resolveColumnWidth, resolveGridEditable } from '../column/meta'
import { resolveHeaderMinWidth } from '../column/resolve-header-min-width'
import {
  countFixedColumns,
  isCustomColumnPinDisabled,
  isCustomColumnToggleDisabled,
  setColumnFixed,
  shouldApplyCustomLive,
} from '../methods/custom'
import { resolveSideBarOptions } from '../utils/side-bar'
import type { DataGrid, GridColumnInfo, GridFixed, GridCustomColumnDraft } from '../types'

const ns = useNamespace('grid-columns-panel')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const store = computed(() => $grid.reactData.customStore)
const sideBar = computed(() => resolveSideBarOptions($grid.options))
const maxFixedCount = computed(() => sideBar.value.maxFixedCount)

const drawerSize = computed(() => `${sideBar.value.width}px`)
const drawerDirection = computed(() => (sideBar.value.position === 'left' ? 'ltr' : 'rtl'))

const listRef = ref<HTMLElement>()
const dragIndex = ref<number | null>(null)
const dragOverIndex = ref<number | null>(null)

const visible = computed({
  get: () => store.value.open,
  set: (val: boolean) => {
    if (!val) {
      $grid.closeCustom?.()
    }
  },
})

const canDrag = computed(() => sideBar.value.draggable)

const toggleableColumns = computed(() =>
  store.value.columns.filter((col) => !isCustomColumnToggleDisabled(col))
)

const checkedCount = computed(
  () => toggleableColumns.value.filter((col) => col.checked !== false).length
)

const toggleableCount = computed(() => toggleableColumns.value.length)

const allChecked = computed(
  () => toggleableCount.value > 0 && checkedCount.value === toggleableCount.value
)

const indeterminate = computed(
  () => checkedCount.value > 0 && checkedCount.value < toggleableCount.value
)

function isToggleDisabled(col: GridColumnInfo) {
  return isCustomColumnToggleDisabled(col)
}

function isPinDisabled(col: GridColumnInfo) {
  return isCustomColumnPinDisabled(col)
}

function columnLabel(col: GridColumnInfo) {
  return col.title || col.field || col.type || ''
}

function resolveWidth(col: GridCustomColumnDraft) {
  return Number(col.width) || resolveColumnWidth(col)
}

function pinOptions(col: GridCustomColumnDraft) {
  const totalFixed = countFixedColumns(store.value.columns)
  const atLimit = totalFixed >= maxFixedCount.value
  const isLeft = col.fixed === 'left'
  const isRight = col.fixed === 'right'

  return [
    {
      label: t('z.table.custom.pinLeft'),
      value: 'left',
      disabled: atLimit && !isLeft,
    },
    {
      label: t('z.table.custom.pinNone'),
      value: '',
    },
    {
      label: t('z.table.custom.pinRight'),
      value: 'right',
      disabled: atLimit && !isRight,
    },
  ]
}

function applyLiveIfNeeded() {
  if (shouldApplyCustomLive($grid)) {
    $grid.applyCustomColumns?.()
  }
}

function onToggleColumn(col: GridCustomColumnDraft, checked: boolean) {
  col.checked = checked
  applyLiveIfNeeded()
}

function toggleAll(checked: boolean) {
  for (const col of toggleableColumns.value) {
    col.checked = checked
  }
  applyLiveIfNeeded()
}

function onWidthChange(col: GridCustomColumnDraft, val: number | undefined) {
  if (val == null || Number.isNaN(val)) {
    return
  }
  const tableEditable = resolveGridEditable($grid.props, $grid.options)
  const headerMin = resolveHeaderMinWidth(col, tableEditable, $grid.gridSize?.value)
  col.width = Math.max(val, headerMin)
  applyLiveIfNeeded()
}

function onFixedChange(col: GridCustomColumnDraft, fixed: GridFixed) {
  const ok = setColumnFixed(col, fixed, store.value.columns, maxFixedCount.value)
  if (!ok) {
    ZMessage.warning(t('z.table.custom.maxFixedWarning', { count: maxFixedCount.value }))
    return
  }
  applyLiveIfNeeded()
}

function moveColumn(from: number, to: number) {
  const list = store.value.columns
  const fromCol = list[from]
  const toCol = list[to]
  if (!fromCol || !toCol || from === to) {
    return
  }
  const next = list.slice()
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  store.value.columns = next
  applyLiveIfNeeded()
}

function onDragStart(index: number, event: DragEvent) {
  if (!canDrag.value) {
    event.preventDefault()
    return
  }
  dragIndex.value = index
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }
}

function onDragOver(index: number, event: DragEvent) {
  if (!canDrag.value || dragIndex.value == null) {
    return
  }
  event.preventDefault()
  dragOverIndex.value = index
}

function onDrop(index: number) {
  if (dragIndex.value == null || dragIndex.value === index) {
    return
  }
  moveColumn(dragIndex.value, index)
  onDragEnd()
}

function onDragEnd() {
  dragIndex.value = null
  dragOverIndex.value = null
}

watch(
  () => store.value.highlightColumnId,
  async (columnId) => {
    if (!columnId || !listRef.value) {
      return
    }
    await nextTick()
    const el = listRef.value.querySelector(`[data-column-id="${columnId}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }
)
</script>
